import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Loader2, Briefcase } from 'lucide-react';
import { IDS } from '@/constants/testIds';
import { useReveal } from '@/hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', phone: '', subject: '', message: '' };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | loading | success | error
  const left = useReveal(0);
  const right = useReveal(120);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'loading', message: '' });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      setStatus({ state: 'success', message: 'Thank you — we will get back to you shortly.' });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        'Unable to submit right now. Please try again.';
      setStatus({ state: 'error', message: typeof msg === 'string' ? msg : 'Please check your inputs.' });
    }
  };

  return (
    <section id="contact" data-testid={IDS.contact.section} className="py-24 md:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        {/* Info side */}
        <div ref={left.ref} className={`${left.className} lg:col-span-5`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-rule" />
            <span className="text-xs tracking-[0.28em] uppercase text-gold-500 font-bold">
              Get in Touch
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy-800 tracking-tight leading-[1.1]">
            Let&rsquo;s build something enduring, together.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-navy-700/80 leading-relaxed">
            Whether it&rsquo;s a tender enquiry, a partnership discussion or a
            supplier proposal — reach out and our team will respond within one
            working day.
          </p>

          <ul className="mt-10 space-y-6">
            <li className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 border border-navy-200 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-gold-500" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold">Office</div>
                <div className="mt-1 text-navy-800 text-base">Plot No. 37-S, Model Town,<br />Hisar, Haryana — 125005, India</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 border border-navy-200 flex items-center justify-center">
                <Phone className="w-5 h-5 text-gold-500" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold">Phone</div>
                <a href="tel:+919996695070" className="mt-1 text-navy-800 text-base hover:text-gold-500">
                  +91 99966 95070
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 border border-navy-200 flex items-center justify-center">
                <Mail className="w-5 h-5 text-gold-500" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold">Email</div>
                <a href="mailto:contact@skinfrastructures.com" className="mt-1 text-navy-800 text-base hover:text-gold-500">
                  contact@skinfrastructures.com
                </a>
              </div>
            </li>
          </ul>

          {/* Careers CTA — civil engineers can send resumes */}
          <div className="mt-10 p-6 border border-navy-200 bg-white/60 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 bg-navy-800 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-gold-400" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.24em] uppercase text-gold-600 font-bold">
                  Careers
                </div>
                <h3 className="font-serif text-xl text-navy-800 mt-1 leading-tight">
                  Civil engineers — join our team.
                </h3>
                <p className="mt-2 text-sm text-navy-700/80">
                  We&rsquo;re actively hiring. Send your resume directly to our
                  founder&rsquo;s inbox.
                </p>
                <a
                  data-testid="careers-send-resume-button"
                  href="mailto:admin@skinfrastructures.com?subject=Civil%20Engineer%20Application%20%E2%80%94%20Resume&body=Dear%20S.K.%20Infrastructure%20Team%2C%0A%0APlease%20find%20my%20resume%20attached%20for%20consideration.%0A%0AName%3A%0APosition%20applied%20for%3A%0AYears%20of%20experience%3A%0ACurrent%20location%3A%0A%0AThank%20you."
                  className="inline-flex items-center gap-2 mt-4 bg-navy-800 hover:bg-navy-900 text-white px-6 py-3 text-[11px] tracking-[0.24em] uppercase font-bold border border-transparent hover:border-gold-400 transition-all"
                >
                  <Mail className="w-4 h-4 text-gold-400" />
                  Send Your Resume
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Form side */}
        <div ref={right.ref} className={`${right.className} lg:col-span-7`}>
          <form
            data-testid={IDS.contact.form}
            onSubmit={submit}
            className="bg-white border border-navy-100 p-8 md:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold mb-2">
                  Full Name *
                </label>
                <input
                  data-testid={IDS.contact.name}
                  required
                  value={form.name}
                  onChange={update('name')}
                  className="w-full border-b border-navy-200 focus:border-gold-500 py-3 text-navy-800 bg-transparent outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold mb-2">
                  Email *
                </label>
                <input
                  data-testid={IDS.contact.email}
                  required
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  className="w-full border-b border-navy-200 focus:border-gold-500 py-3 text-navy-800 bg-transparent outline-none transition-colors"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold mb-2">
                  Phone
                </label>
                <input
                  data-testid={IDS.contact.phone}
                  value={form.phone}
                  onChange={update('phone')}
                  className="w-full border-b border-navy-200 focus:border-gold-500 py-3 text-navy-800 bg-transparent outline-none transition-colors"
                  placeholder="+91"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold mb-2">
                  Subject
                </label>
                <input
                  data-testid={IDS.contact.subject}
                  value={form.subject}
                  onChange={update('subject')}
                  className="w-full border-b border-navy-200 focus:border-gold-500 py-3 text-navy-800 bg-transparent outline-none transition-colors"
                  placeholder="Tender enquiry, partnership, etc."
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.24em] uppercase text-navy-500 font-bold mb-2">
                Message *
              </label>
              <textarea
                data-testid={IDS.contact.message}
                required
                rows={5}
                value={form.message}
                onChange={update('message')}
                className="w-full border border-navy-200 focus:border-gold-500 p-4 text-navy-800 bg-transparent outline-none transition-colors resize-none"
                placeholder="Tell us about your project or enquiry…"
              />
            </div>

            {status.state === 'success' && (
              <div
                data-testid={IDS.contact.success}
                className="flex items-start gap-3 p-4 bg-gold-50 border border-gold-200 text-navy-800"
              >
                <CheckCircle2 className="w-5 h-5 text-gold-600 shrink-0 mt-0.5" />
                <span className="text-sm">{status.message}</span>
              </div>
            )}
            {status.state === 'error' && (
              <div
                data-testid={IDS.contact.error}
                className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800"
              >
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <span className="text-sm">{status.message}</span>
              </div>
            )}

            <button
              data-testid={IDS.contact.submit}
              type="submit"
              disabled={status.state === 'loading'}
              className="inline-flex items-center gap-3 bg-navy-800 hover:bg-navy-900 disabled:opacity-60 text-white px-8 py-4 text-xs tracking-[0.24em] uppercase font-bold border border-transparent hover:border-gold-400 transition-all"
            >
              {status.state === 'loading' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <Send className="w-4 h-4 text-gold-400" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
